// src/onboarding/OnboardingPlant.jsx
// Phase 37 — extracted from src/App.jsx (Plan 37-03).
//
// Scope: Step 3 — Plant (Phase 31.3 D-05). Wraps AddPlantForm in OnbFrame with
// blurred planted-border background + plant-name-tinted spotlight flower.
// AddPlantForm itself unchanged — Phase 24 ONB-11 simplification preserved.
//
// AddPlantForm now lives at src/sheets/AddPlantForm.jsx (Plan 37-05);
// imported as a default below — this closes the cross-folder flag from Plan 37-03.

import AddPlantForm from '../sheets/AddPlantForm.jsx';
import OnbFrame from './OnbFrame.jsx';
import { onbResolveSpotlightColors } from './spotlight.js';

export default function OnboardingPlant({plants, areas, newPlant, setNewPlant, onAddPlant, features, aiReady, aiStatus = 'ready', onAiFailure, onContinue, step = 4, total = 5}) {
  const activeColors = onbResolveSpotlightColors(newPlant?.name || "");

  const bgExtra = (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
      <svg viewBox="0 0 402 874" preserveAspectRatio="xMidYMax slice"
           style={{ position: "absolute", inset: 0, width: "100%", height: "100%", filter: "blur(1.5px)" }}>
        {/* ground */}
        <rect x="0" y="620" width="402" height="254" fill="#9cb873" opacity="0.35"/>
        <rect x="0" y="700" width="402" height="174" fill="#7a5a3d" opacity="0.3"/>
        {/* bushy background plants */}
        {[30,100,165,240,315,370].map((cx, i) => (
          <g key={i} opacity={0.55}>
            <ellipse cx={cx} cy={640 + (i%3)*10} rx={34+(i%3)*6} ry={22+(i%2)*4} fill={i%2 ? "#6b8a55" : "#5a7a4a"}/>
            <ellipse cx={cx+4} cy={630 + (i%3)*10} rx={24+(i%3)*4} ry={16+(i%2)*3} fill="#7a9865" opacity="0.7"/>
          </g>
        ))}
        {/* spotlight flower — tinted by plant-name match */}
        <g transform="translate(201, 630)" opacity="0.9">
          <ellipse cx="0" cy="28" rx="48" ry="10" fill="rgba(60,40,20,0.2)"/>
          <path d="M -2 28 Q -4 10 0 -6" stroke={activeColors[1]} strokeWidth="3" fill="none"/>
          <circle cx="0" cy="-10" r="18" fill={activeColors[0]} opacity="0.9"/>
          <circle cx="-14" cy="-4" r="10" fill={activeColors[0]} opacity="0.8"/>
          <circle cx="14" cy="-4" r="10" fill={activeColors[0]} opacity="0.8"/>
          <circle cx="0" cy="-22" r="10" fill={activeColors[0]} opacity="0.8"/>
          <circle cx="0" cy="-10" r="6" fill={activeColors[1]}/>
        </g>
      </svg>
    </div>
  );

  return (
    <OnbFrame step={step} total={total} ground="dawn" bgExtra={bgExtra}>
      <div className="onb-content">
        <div style={{ height: 14 }}/>
        <p className="onb-eyebrow">The first resident</p>
        <h1 className="onb-heading">What's <em>already</em> growing in your spaces?</h1>
        <p className="onb-sub">Name one plant you tend. We'll build a year of care around it — when to feed, prune, divide, deadhead.</p>

        <div style={{ marginTop: 14 }}>
          <AddPlantForm
            plant={newPlant}
            onChange={setNewPlant}
            onAdd={(schedule, colour, meta) => {
              // WR-02: only advance the wizard when addPlant actually added a plant.
              // addPlant returns the new plant id on success, or null for empty name / cap reached.
              // AddPlantForm's own validation surfaces the error when we stay on this step.
              // Phase 34 / RES-01 / D-03 — pass through meta.usedFallback so the new
              // plant carries addedVia:'fallback-default' if AI failed mid-flow.
              const newId = onAddPlant(schedule, colour, meta);
              if (newId != null) requestAnimationFrame(() => onContinue());
            }}
            onCancel={undefined /* hidden via compact */}
            areas={areas}
            aiReady={aiReady}
            aiStatus={aiStatus}
            onAiFailure={onAiFailure}
            features={features}
            compact={true}
          />
        </div>
      </div>
    </OnbFrame>
  );
}
